import React, { type ReactNode } from 'react';

import { AnimatePresence } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';

import WrappedGIft from '@pkg/assets/images/v2/emoji/wrapped-gift.png';
import { Modal } from '@pkg/components';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { Sizes } from '@pkg/components/Sections/Profile/Buttons/Button/types';
import {
    DepActionBanner,
    DepActionBannerTextHighlight,
    getActionBannerSuffixColor,
    getActionBannerSuffixVariant,
} from '@pkg/components/deprecated';
import { useBreakpoint, useInviteModal } from '@pkg/hooks';
import { useGenerateInvite } from '@pkg/hooks/deprecated';

import { Typography } from '../../../../DesignSystem';

export interface InviteToYatModalProps {
    children?: ReactNode;
}

export function InviteToYatModal({ children }: InviteToYatModalProps) {
    const { t: translate } = useTranslation('invites');
    const { isWithinBreakpoint: isMobileOrSmaller } = useBreakpoint('mobile');
    const { closeModal } = useInviteModal();

    const {
        getCtaActionContent,
        ctaAriaLabel,
        inviteGenerated,
        handleGenerateInvite,
        invitesRemaining,
        inviteGenerationDisabled,
    } = useGenerateInvite();

    return (
        <AnimatePresence>
            <Modal padded onClose={closeModal}>
                {isMobileOrSmaller && <Modal.Header>{translate('invite_title')}</Modal.Header>}
                <Modal.Layout>
                    <Modal.Layout.Section>
                        <img
                            src={WrappedGIft}
                            alt="Gift"
                            // todo style this better
                            style={{ height: isMobileOrSmaller ? 100 : 140, marginTop: isMobileOrSmaller ? '25%' : 0 }}
                        />
                    </Modal.Layout.Section>
                    <Modal.Layout.Section padded>
                        <Typography
                            size={isMobileOrSmaller ? 30 : 40}
                            type="headline"
                            variant="span"
                            align="center"
                            weight="700"
                        >
                            {translate('invite_prompt_title')}
                        </Typography>
                        <Typography
                            size={isMobileOrSmaller ? 16 : 20}
                            // TODO: `color` should be a token!
                            color={'#FFFFFF80'}
                            type="paragraph"
                            variant="span"
                            align="center"
                            weight={'600'}
                        >
                            <Trans ns="invites" i18nKey="invite_prompt_paragraph">
                                <span style={{ color: '#fff' }}>0</span>
                            </Trans>
                        </Typography>
                    </Modal.Layout.Section>

                    {children}

                    <Modal.Layout.Section alignEnd={!isMobileOrSmaller}>
                        <DepActionBanner
                            actionContent={getCtaActionContent()}
                            ariaLabel={ctaAriaLabel}
                            disabled={inviteGenerationDisabled}
                            success={inviteGenerated && !inviteGenerationDisabled}
                            onAction={() => handleGenerateInvite()}
                            suffix={!isMobileOrSmaller}
                        >
                            <Typography
                                type={isMobileOrSmaller ? 'label' : 'paragraph'}
                                size={isMobileOrSmaller ? 14 : 16}
                                variant="span"
                            >
                                <Trans ns="invites" i18nKey="invites_remaining" count={invitesRemaining}>
                                    <DepActionBannerTextHighlight $error={invitesRemaining === 0}>
                                        0
                                    </DepActionBannerTextHighlight>
                                </Trans>
                            </Typography>
                        </DepActionBanner>
                    </Modal.Layout.Section>

                    {isMobileOrSmaller && (
                        <Modal.Layout.Section alignEnd>
                            <Button
                                fluid
                                size={Sizes.large}
                                aria-label={ctaAriaLabel}
                                disabled={inviteGenerationDisabled}
                                onClick={() => handleGenerateInvite()}
                                color={getActionBannerSuffixColor({
                                    disabled: inviteGenerationDisabled,
                                    success: inviteGenerated && !inviteGenerationDisabled,
                                })}
                                variant={getActionBannerSuffixVariant({ disabled: inviteGenerationDisabled })}
                            >
                                {getCtaActionContent()}
                            </Button>
                        </Modal.Layout.Section>
                    )}
                </Modal.Layout>
            </Modal>
        </AnimatePresence>
    );
}
